import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { BarChart } from '@mui/x-charts/BarChart';
import { type ReactNode, useState } from 'react';
import { ReportCard, ReportSection } from '../ReportCard';
import type { GroupedTags, TagCount } from './data/api';

const LABEL_CHARS = 12;
const SCENES = [
  { key: 'hookScene', label: 'Hook scene', prefix: 'Hook Scene ' },
  { key: 'core', label: 'Full creative', prefix: '' },
  { key: 'endCard', label: 'End card', prefix: 'End Card ' },
] as const;

type SceneKey = (typeof SCENES)[number]['key'];

interface CommonTagsProps {
  title: string;
  description: string;
  tags: GroupedTags;
}

function truncate(text: string): string {
  return text.length > LABEL_CHARS ? `${text.slice(0, LABEL_CHARS - 1)}…` : text;
}

function tagLabel(row: TagCount, prefix: string): string {
  const feature =
    prefix !== '' && row.feature.startsWith(prefix)
      ? row.feature.slice(prefix.length)
      : row.feature;
  return `${feature}: ${row.tag}`;
}

/** Bar chart of how many creatives carry each tag, switchable between scenes. */
export function CommonTags({ title, description, tags }: CommonTagsProps): ReactNode {
  const theme = useTheme();
  const [scene, setScene] = useState<SceneKey>('hookScene');
  const active = SCENES.find((entry) => entry.key === scene) ?? SCENES[0];
  const rows = tags[scene];
  const labels = rows.map((row) => tagLabel(row, active.prefix));

  return (
    <ReportSection
      title={title}
      description={description}
      action={
        <Select
          size="small"
          value={scene}
          onChange={(event) => setScene(event.target.value as SceneKey)}
          sx={{ fontSize: 13, minWidth: 140, bgcolor: 'background.paper' }}
        >
          {SCENES.map((entry) => (
            <MenuItem key={entry.key} value={entry.key} sx={{ fontSize: 13 }}>
              {entry.label}
            </MenuItem>
          ))}
        </Select>
      }
    >
      <ReportCard label="Creatives per tag">
        {rows.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: 'text.secondary', py: 6, textAlign: 'center' }}>
            No tags for this scene.
          </Typography>
        ) : (
          <BarChart
            height={300}
            hideLegend
            series={[
              {
                data: rows.map((row) => row.creatives),
                label: 'Creatives',
                color: theme.palette.primary.main,
              },
            ]}
            xAxis={[
              {
                data: labels,
                scaleType: 'band',
                categoryGapRatio: 0.2,
                valueFormatter: (value: string, context) =>
                  context.location === 'tick' ? truncate(value) : value,
                tickLabelStyle: { fontSize: 11 },
              },
            ]}
            yAxis={[{ label: 'Number of Creatives', tickMinStep: 1 }]}
          />
        )}
      </ReportCard>
    </ReportSection>
  );
}
